import React, { useState } from 'react';
import { Form, Input, Button, Typography, Flex } from 'antd';
import { getResetPasswordScheme } from '../model/scheme';
import { useRequestPasswordResetMutation } from '../api/resetPasswordApi';
import { useIntl } from 'react-intl';
import CustomCardContainer from 'shared/ui/CustomCardContainer/CustomCardContainer';
import classes from './ResetPasswordPage.module.scss';
import { ReactComponent as CheckmarkIcon } from 'shared/assets/svg/checkmark.svg';
import { ReactComponent as ExclamationIcon } from 'shared/assets/svg/exclamation.svg';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from 'shared/const/routes';

const ForgotPasswordForm = () => {
  const [form] = Form.useForm();
  const { formatMessage: t } = useIntl();
  const [requestReset, { isLoading }] = useRequestPasswordResetMutation();
  const [step, setStep] = useState<'form' | 'success' | 'error'>('form');
  const [email, setEmail] = useState<string>('');
  const navigate = useNavigate();

  const onFinish = async ({ email }: { email: string }) => {
    try {
      await requestReset({ email }).unwrap();
      setEmail(email);
      setStep('success');
    } catch {
      setEmail(email);
      setStep('error');
    }
  };

  const renderSuccess = () => (
    <Flex className={classes.wrapper} flex={1} justify="center" align="center">
      <CustomCardContainer className={classes.card}>
        <CheckmarkIcon className={classes['icon-success']} />
        <Typography.Text className={classes.text} style={{ textAlign: 'center'}}>
          {t({ id: 'screens.resetPassword.request.success.check' }) + '\n'}
          <strong>{email}</strong> {t({ id: 'screens.resetPassword.request.success.reset' })} 
        </Typography.Text>
        <Button onClick={() => navigate(ROUTES.LOGIN.path)} type="primary" block style={{ marginTop: '16px' }}>
          {t({ id: 'sections.send.swift.success.button' })}
        </Button>
      </CustomCardContainer>
    </Flex>
  );

  const renderError = () => (
    <Flex className={classes.wrapper} flex={1} justify="center" align="center">
      <CustomCardContainer className={classes.card}>
        <ExclamationIcon className={classes['icon-error']} />
        <Typography.Text className={classes.text}>
          E-mail: <strong>{email}</strong> {t({ id: 'screens.auth.email.messages.not_found' })}
        </Typography.Text>
        <Typography.Text className={classes.desc}>
          {t({ id: 'screens.auth.email.messages.contact_support' })}
        </Typography.Text>
        <Button href="mailto:cardservices@weststeincard.com" type="primary" block>
          {t({ id: 'river.sheets.accountSoftBlocked.button' })}
        </Button>
      </CustomCardContainer>
    </Flex>
  );

  const renderForm = () => (
    <div className={classes.wrapper}>
      <CustomCardContainer className={classes.card}>
        <div className={classes.companyName}>Weststein</div>

        <div className={classes.title}>
          {t({ id: 'forgot.password.title', defaultMessage: 'Forgot your password?' })}
        </div>

        <div className={classes.desc}>
          {t({
            id: 'forgot.password.subtitle',
            defaultMessage: 'No problem, we’ll send instructions to your inbox to reset your password.',
          })}
        </div>

        <Form form={form} onFinish={onFinish} layout="vertical" style={{ width: '100%' }}>
          <Form.Item name="email" rules={getResetPasswordScheme(t).email}>
            <Input placeholder="Email" />
          </Form.Item>
          <Button type="primary" htmlType="submit" loading={isLoading} block>
            {t({ id: 'buttons.reset.password', defaultMessage: 'Reset password' })}
          </Button>
        </Form>
      </CustomCardContainer>
    </div>
  );

  if (step === 'success') return renderSuccess();
  if (step === 'error') return renderError();
  return renderForm();
};

export default ForgotPasswordForm;
